import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Typography,
  Avatar,
  IconButton,
  useMediaQuery,
} from '@mui/material';
import {
  WorkspacePremium,
  WorkspacePremiumOutlined,
} from '@mui/icons-material';
import { likeActivityCall } from '../../services/company_services';
import { useAccount } from '../../contexts/Account';
import { AccountType } from '../../services/utils/types';
import { timeAgo } from '../../utils/dateFormat';
import { ReactComponent as LikeHollowIcon } from '../../icons/like-hollow.svg';
import { ReactComponent as LikeIcon } from '../../icons/like.svg';
import DefaultCompany from '../../icons/building_round.svg';
import eventBus, { eventBusValues } from '../../eventBus';
import SurveyStats from './SurveyStats';
import { addBreakthroughIdea } from '../../services/advisor_services';
import BreakthroughBadge from '../Advisor/BreakthroughBadge';

export default function BoardroomActivityCard({
  company,
  activity,
  breakthroughsEnabled,
}) {
  const isSM = useMediaQuery((theme) => theme.breakpoints.down('sm'));
  const {
    accountDetails,
    asLoggedUserDetails,
    type,
    isImpersonated,
    currentUserInfo,
  } = useAccount();
  const [likes, setLikes] = useState(activity.likes || []);
  const [isIdeaExpanded, setIsIdeaExpanded] = useState(false);
  const [isBreakthrough, setIsBreakthrough] = useState(
    activity.idea ? activity.idea.isBreakthrough : false
  );
  const [waitForCall, setWaitForCall] = useState(false);
  const isBreakthroughAdvisor = useMemo(() => {
    if (!breakthroughsEnabled) return false;
    return (
      accountDetails?.advisorsWithBreakthroughs?.includes(activity.advisorId) ||
      company?.advisorsWithBreakthroughs?.includes(activity.advisorId)
    );
  }, [accountDetails, activity, company]);
  const timeAgoString = timeAgo(activity.createdAt);
  const [isAlreadyLiked, setIsAlreadyLiked] = useState(
    activity.likes
      ? activity.likes.some(
          (element) => element.userId === asLoggedUserDetails.userId
        )
      : false
  );

  useEffect(() => {
    setIsIdeaExpanded(false);
  }, [activity.id]);

  useEffect(() => {
    let timer;
    if (waitForCall) {
      timer = setTimeout(() => {
        setWaitForCall(false);
      }, 5000);
    }
    return () => clearTimeout(timer);
  }, [waitForCall]);

  function activityLikeAction() {
    if (activity.companyId === accountDetails.id) {
      // setErrorMessage('Not allowed to like your own content');
      // setShowErrorSnackbar(true);
    } else {
      const likeDTO = {
        type:
          type === AccountType.COMPANY
            ? AccountType.CONTACT
            : AccountType.ADVISOR,
        name: asLoggedUserDetails.displayName,
        userId: asLoggedUserDetails.userId,
      };
      addActivityLike(likeDTO);
      likeActivityCall({
        ACCOUNT_TYPE:
          type === AccountType.COMPANY
            ? AccountType.CONTACT
            : AccountType.ADVISOR,
        USER_ID: asLoggedUserDetails.userId,
        ACTIVITY_ID: activity.id,
      });
    }
  }

  function addActivityLike(likeDTO) {
    if (!isAlreadyLiked) {
      setLikes([...likes, likeDTO]);
    } else {
      setLikes(
        likes.filter((element) => element.userId !== asLoggedUserDetails.userId)
      );
    }
    setIsAlreadyLiked(!isAlreadyLiked);
  }

  function updateBreakthrough() {
    if (waitForCall || type === AccountType.ADVISOR) return;
    setWaitForCall(true);
    addBreakthroughIdea({
      userType: isImpersonated ? 'Admin' : 'Company',
      userId: currentUserInfo.username,
      ideaId: activity.idea.id,
      isBreakthrough: !isBreakthrough,
      companyActivityId: activity.id,
    });
    setIsBreakthrough((prev) => !prev);
    if (
      !accountDetails.advisorsWithBreakthroughs?.includes(activity.advisorId)
    ) {
      accountDetails.advisorsWithBreakthroughs.push(activity.advisorId);
    }
  }

  const getHeaderStyle = () => {
    switch (activity.activityType) {
      case 'advisorJoinedToCompany':
        return {
          backgroundColor: '#D7DAFD',
          color: '#001030',
        };
      case 'advisorIdea':
        return {
          backgroundColor: '#4FB5F9',
          color: 'white',
        };
      case 'surveyResponse':
        return {
          backgroundColor: '#F6F6F6',
          color: '#232B35',
        };
      case 'surveyCreated':
        return {
          backgroundColor: '#2d74f2',
          color: 'white',
        };
    }
    return 'Activity';
  };
  const headerStyle = getHeaderStyle();
  const getHeaderText = () => {
    switch (activity.activityType) {
      case 'advisorJoinedToCompany':
        return 'New Board Member';
      case 'advisorIdea':
        return `Priority: “${activity.idea?.currentGoal}”`;
      case 'surveyResponse':
        return 'New Survey Response';
      case 'surveyCreated':
        return 'Boardroom Survey';
    }
    return 'Activity';
  };

  const getAvatarUrl = (activity) => {
    if (
      activity.activityType === 'surveyResponse' ||
      activity.activityType === 'surveyCreated'
    ) {
      return company?.image?.id
        ? `${process.env.REACT_APP_IMAGES_URL}${company.image.id}`
        : DefaultCompany;
    }
    return `${process.env.REACT_APP_IMAGES_URL}${activity.advisor?.image?.imageKey}`;
  };

  function openProfile(advisorId) {
    if (!advisorId) {
      return;
    }
    eventBus.dispatch(eventBusValues.triggerAdvisorDrawerAction, {
      fetchAdvisor: true,
      id: advisorId,
      boardRoomView: true,
      isBreakthroughAdvisor:
        breakthroughsEnabled &&
        company?.advisorsWithBreakthroughs?.includes(advisorId),
    });
  }

  const getCardBody = () => {
    if (activity.activityType === 'advisorIdea') {
      return activity.idea?.idea;
    } else if (activity.activityType === 'advisorJoinedToCompany') {
      return activity.relationship?.introduction;
    } else if (activity.activityType === 'surveyResponse') {
      return activity.survey?.question;
    } else if (activity.activityType === 'surveyCreated') {
      return activity.surveyQuestion?.[0]?.body || 'New Survey Created';
    }
    return '';
  };

  const trimTrailingEmptyLines = (linesArray) => {
    if (!linesArray || linesArray.length === 0) {
      return [];
    }
    let lastNonEmptyIndex = linesArray.length - 1;
    while (lastNonEmptyIndex >= 0 && linesArray[lastNonEmptyIndex] === '') {
      lastNonEmptyIndex -= 1;
    }
    return linesArray.slice(0, lastNonEmptyIndex + 1);
  };

  const getFirstNWordsText = (text, maxWords) => {
    if (!text) {
      return '';
    }
    const wordRegex = /\S+/g;
    let match;
    let wordsSeen = 0;
    let lastIndex = 0;

    while ((match = wordRegex.exec(text)) !== null) {
      wordsSeen += 1;
      lastIndex = wordRegex.lastIndex;
      if (wordsSeen >= maxWords) {
        break;
      }
    }
    if (wordsSeen < maxWords || lastIndex >= text.length) {
      return text;
    }
    return text.slice(0, lastIndex).trimEnd();
  };

  const advisorIdeaText =
    activity.activityType === 'advisorIdea' ? activity.idea?.idea || '' : '';
  const rawAdvisorIdeaLines = advisorIdeaText
    ? advisorIdeaText.split(/\r\n|\r|\n/)
    : [];
  const advisorIdeaLines = trimTrailingEmptyLines(rawAdvisorIdeaLines);
  const advisorIdeaWordCount = advisorIdeaText
    ? (advisorIdeaText.match(/\S+/g) || []).length
    : 0;

  const hasLongAdvisorIdeaLines = advisorIdeaLines.length > 10;
  const hasLongAdvisorIdeaWords =
    !hasLongAdvisorIdeaLines && advisorIdeaWordCount > 200;
  let collapsedIdeaLines = advisorIdeaLines;
  let isIdeaTruncated = false;

  if (hasLongAdvisorIdeaLines) {
    collapsedIdeaLines = advisorIdeaLines.slice(0, 10);
    isIdeaTruncated = true;
  } else if (hasLongAdvisorIdeaWords) {
    const truncatedText = getFirstNWordsText(advisorIdeaText, 200);
    collapsedIdeaLines = trimTrailingEmptyLines(
      truncatedText.split(/\r\n|\r|\n/)
    );
    isIdeaTruncated = true;
  }

  const advisorIdeaLinesToDisplay = isIdeaExpanded
    ? advisorIdeaLines
    : collapsedIdeaLines;

  return (
    <>
      <Box
        sx={{
          width: '100%',
          borderRadius: '10px',
          bgcolor: 'background.paper',
          mt: 1,
          p: 1,
        }}
      >
        <Box
          sx={{
            ...headerStyle,
            borderRadius: '10px 10px 0 0',
            m: '-8px',
            py: '6px',
            px: '8px',
            textAlign: 'center',
          }}
        >
          <Typography
            sx={{
              // whiteSpace: 'nowrap',
              // overflow: 'hidden',
              // textOverflow: 'ellipsis',
              fontWeight: '600',
              px: isSM ? '20px' : '45px',
            }}
          >
            {getHeaderText()}
          </Typography>
        </Box>
        <Box sx={{ p: '18px', pb: '10px' }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              mb: 1,
            }}
          >
            {breakthroughsEnabled && isBreakthroughAdvisor ? (
              <Box
                sx={{
                  display: 'inline-block',
                  width: '44px',
                  height: '44px',
                  borderRadius: '50%',
                  background:
                    'linear-gradient(180deg, #4DF4FF 0%, rgba(38, 83, 242, 0.69) 43.75%, rgba(61, 19, 182, 0.63) 80.21%, #6736FA 100%)',
                  padding: '2px',
                  mr: '10px',
                }}
              >
                <Avatar
                  src={getAvatarUrl(activity)}
                  onClick={() =>
                    openProfile(activity.advisor?.id || activity.advisorId)
                  }
                  alt={activity.advisor?.displayName}
                  sx={{ width: 40, height: 40, mr: 2, cursor: 'pointer' }}
                />
              </Box>
            ) : (
              <Avatar
                src={getAvatarUrl(activity)}
                onClick={() =>
                  openProfile(activity.advisor?.id || activity.advisorId)
                }
                alt={activity.advisor?.displayName}
                sx={{ width: 40, height: 40, mr: 2, cursor: 'pointer' }}
              />
            )}
            {activity.activityType != 'surveyResponse' &&
            activity.activityType != 'surveyCreated' ? (
              <Box>
                <Typography
                  sx={{
                    fontWeight: '500',
                    color: '#232B35',
                    fontSize: '14px',
                    lineHeight: '32px',
                    cursor: 'pointer',
                  }}
                  onClick={() =>
                    openProfile(activity.advisor?.id || activity.advisorId)
                  }
                >
                  {activity.advisor?.displayName}
                </Typography>
                <Typography
                  sx={{
                    fontWeight: '500',
                    color: '#74879A',
                    fontSize: '10px',
                    lineHeight: '15px',
                    letterSpacing: '0.75px',
                  }}
                >
                  Advisor
                  {/* Advisor - {timeAgoString} */}
                </Typography>
              </Box>
            ) : activity.activityType == 'surveyResponse' ? (
              <Box>
                <Typography
                  sx={{
                    fontWeight: '500',
                    color: '#232B35',
                    fontSize: '14px',
                    lineHeight: '32px',
                  }}
                >
                  <Box
                    component="span"
                    sx={{
                      cursor: 'pointer',
                    }}
                    onClick={() =>
                      openProfile(activity.advisor?.id || activity.advisorId)
                    }
                  >
                    {activity.survey.userName}
                  </Box>{' '}
                  {`answered ${activity.survey.response}`}
                </Typography>
              </Box>
            ) : (
              <Box>
                <Typography
                  sx={{
                    fontWeight: '500',
                    color: '#232B35',
                    fontSize: '14px',
                    lineHeight: '32px',
                  }}
                >
                  {company?.displayName}
                </Typography>
              </Box>
            )}
            {isBreakthroughAdvisor && (
              <BreakthroughBadge
                iconSX={{
                  marginLeft: '5px',
                  marginBottom: '6px',
                  position: 'initial',
                }}
              />
            )}
          </Box>

          {activity.activityType === 'advisorIdea' ? (
            <Typography
              component="div"
              sx={{
                whiteSpace: 'pre-line',
                fontWeight: '400',
                color: '#232B35',
                fontSize: '14px',
                lineHeight: '24px',
              }}
            >
              {advisorIdeaLinesToDisplay.map((line, index) => (
                <React.Fragment key={`idea-line-${index}`}>
                  {line}
                  {index < advisorIdeaLinesToDisplay.length - 1 && <br />}
                </React.Fragment>
              ))}
              {isIdeaTruncated && !isIdeaExpanded && (
                <>
                  {advisorIdeaLinesToDisplay.length > 0 && <br />}
                  <Box
                    component="span"
                    sx={{ color: '#2d74f2', cursor: 'pointer' }}
                    onClick={() => setIsIdeaExpanded(true)}
                  >
                    …Read more
                  </Box>
                </>
              )}
              {isIdeaTruncated && isIdeaExpanded && (
                <>
                  {advisorIdeaLinesToDisplay.length > 0 && <br />}
                  <Box
                    component="span"
                    sx={{ color: '#2d74f2', cursor: 'pointer' }}
                    onClick={() => setIsIdeaExpanded(false)}
                  >
                    Show less
                  </Box>
                </>
              )}
            </Typography>
          ) : (
            <Typography
              sx={{
                whiteSpace: 'pre-line',
                fontWeight: '400',
                color: '#232B35',
                fontSize: '14px',
                lineHeight: '24px',
              }}
            >
              {getCardBody()}
            </Typography>
          )}
          {activity.activityType === 'surveyCreated' && (
            <SurveyStats surveyId={activity.surveyId} />
          )}
        </Box>
      </Box>
      <Box display="flex" flexDirection="row">
        <Box
          sx={{
            py: 0,
            display: 'flex',
            alignItems: 'left',
            cursor: 'pointer',
          }}
          onClick={activityLikeAction}
        >
          <IconButton size="small" aria-label="like">
            {isAlreadyLiked ? (
              <LikeIcon style={{ width: '20px', height: '20px' }} />
            ) : (
              <LikeHollowIcon style={{ width: '20px', height: '20px' }} />
            )}
          </IconButton>
          <Typography
            sx={{
              p: '5px',
              fontSize: '18px',
              fontWeight: '600',
              color: '#232B35',
              letterSpacing: '0.75px',
              lineHeight: '32px',
            }}
          >
            {likes.length} {likes.length === 1 ? 'like' : 'likes'}
          </Typography>
        </Box>
        {(type === AccountType.COMPANY ||
          (type === AccountType.ADVISOR && isBreakthrough)) &&
          breakthroughsEnabled &&
          activity.activityType === 'advisorIdea' && (
            <Box
              sx={{
                py: 0,
                display: 'flex',
                alignItems: 'left',
                cursor: type === AccountType.COMPANY ? 'pointer' : 'default',
              }}
              onClick={updateBreakthrough}
            >
              <IconButton
                size="small"
                aria-label="like"
                disabled={type === AccountType.ADVISOR}
              >
                {isBreakthrough ? (
                  <WorkspacePremium
                    style={{ width: '20px', height: '20px', color: '#EFBF04' }}
                  />
                ) : (
                  <WorkspacePremiumOutlined
                    style={{ width: '20px', height: '20px', color: '#3171f6' }}
                  />
                )}
              </IconButton>
              <Typography
                sx={{
                  p: '5px',
                  fontSize: '18px',
                  fontWeight: '600',
                  color: '#232B35',
                  letterSpacing: '0.75px',
                  lineHeight: '32px',
                }}
              >
                Breakthrough
              </Typography>
            </Box>
          )}
      </Box>
    </>
  );
}
